import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search, Pencil, Trash2, Eye, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { documentsApi, DOC_GROUPS, DOC_STATUSES, FINANCIAL_TYPES, docBalance } from "@/api";
import { useLang, fmtMoney, fmtDate } from "@/i18n";
import { StatusBadge, TypeBadge } from "@/components/Badges";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { cn } from "@/lib/utils";

const GROUPS = ["all", "sales", "purchases"];
const GROUP_STYLE = {
  all: "data-[active=true]:bg-slate-800 data-[active=true]:text-white dark:data-[active=true]:bg-slate-200 dark:data-[active=true]:text-slate-900",
  sales: "data-[active=true]:bg-blue-800 data-[active=true]:text-white",
  purchases: "data-[active=true]:bg-orange-600 data-[active=true]:text-white",
};

export default function Documents() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const contactId = params.get("contact_id") || undefined;
  const group = params.get("group") || "all";
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [deleting, setDeleting] = useState(null);
  const groupTypes = group === "all" ? [...DOC_GROUPS.sales, ...DOC_GROUPS.purchases] : DOC_GROUPS[group];

  const setGroup = (g) => {
    const next = Object.fromEntries(params.entries());
    if (g === "all") delete next.group; else next.group = g;
    setParams(next); setType("all");
  };

  const load = useCallback(() => {
    documentsApi.list({ q: q || undefined, type, status, group: group === "all" ? undefined : group, contact_id: contactId }).then(setDocs).catch(() => toast.error(t("error")));
  }, [q, type, status, group, contactId, t]);

  useEffect(() => { const id = setTimeout(load, 200); return () => clearTimeout(id); }, [load]);

  const remove = async () => {
    try { await documentsApi.remove(deleting.id); toast.success(t("doc_deleted")); load(); }
    catch { toast.error(t("error")); }
    finally { setDeleting(null); }
  };

  const contactName = contactId && docs[0]?.contact_name;
  const totals = docs.reduce((a, d) => (d.status !== "cancelled" ? { total: a.total + d.total, balance: a.balance + (FINANCIAL_TYPES.includes(d.type) ? docBalance(d) : 0) } : a), { total: 0, balance: 0 });

  return (
    <div className="space-y-5 page-enter" data-testid="documents-page">
      <div className="inline-flex rounded-md border border-slate-200 dark:border-slate-700 p-1 bg-white dark:bg-slate-900" data-testid="documents-group-tabs">
        {GROUPS.map((g) => (
          <button key={g} type="button" onClick={() => setGroup(g)} data-active={group === g} data-testid={`documents-group-${g}`}
            className={cn("px-4 h-8 rounded text-sm font-semibold transition-colors text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800", GROUP_STYLE[g])}>
            {t(`group_${g}`)}
          </button>
        ))}
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center gap-3 justify-between">
        <div className="flex flex-col sm:flex-row gap-2 flex-1 max-w-3xl">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="pl-9" data-testid="documents-search-input" />
          </div>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="sm:w-56" data-testid="documents-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all_types")}</SelectItem>
              {groupTypes.map((v) => <SelectItem key={v} value={v}>{t(`doc_${v}`)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="sm:w-48" data-testid="documents-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all_statuses")}</SelectItem>
              {DOC_STATUSES.map((v) => <SelectItem key={v} value={v}>{t(`status_${v}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          {group !== "purchases" && (
            <Button onClick={() => navigate("/documents/new?type=invoice")} className="bg-blue-800 hover:bg-blue-700" data-testid="create-sale-button">
              <Plus size={16} className="mr-1" /> {t("new_sale")}
            </Button>
          )}
          {group !== "sales" && (
            <Button onClick={() => navigate("/documents/new?type=purchase")} className="bg-orange-600 hover:bg-orange-500" data-testid="create-purchase-button">
              <Plus size={16} className="mr-1" /> {t("new_purchase")}
            </Button>
          )}
        </div>
      </div>

      {contactId && (
        <div className="inline-flex items-center gap-2 text-sm bg-blue-50 dark:bg-blue-950/40 text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-800 rounded px-3 py-1.5" data-testid="contact-filter-chip">
          {t("documents_of")}: <strong>{contactName || "…"}</strong>
          <button onClick={() => setParams({})} className="hover:text-rose-600" data-testid="clear-contact-filter"><X size={14} /></button>
        </div>
      )}

      <div className="erp-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm erp-table">
            <thead>
              <tr className="text-left">
                <th className="px-4 py-3">{t("number")}</th>
                <th className="px-4 py-3">{t("type")}</th>
                <th className="px-4 py-3">{t("contact")}</th>
                <th className="px-4 py-3 hidden md:table-cell">{t("issue_date")}</th>
                <th className="px-4 py-3 hidden lg:table-cell">{t("due_date")}</th>
                <th className="px-4 py-3">{t("status")}</th>
                <th className="px-4 py-3 text-right">{t("total")}</th>
                <th className="px-4 py-3 text-right hidden md:table-cell">{t("balance")}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {docs.map((d) => (
                <tr key={d.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`document-row-${d.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/documents/${d.id}`} className="font-mono font-medium text-blue-800 dark:text-blue-300 hover:underline" data-testid={`document-number-${d.id}`}>{d.number}</Link>
                    {d.supplier_number && <div className="text-xs text-slate-500 font-mono">{d.supplier_number}</div>}
                    {d.mydata?.mark && ["sent", "mock"].includes(d.mydata.status) && <div className="text-[10px] font-mono text-emerald-700 dark:text-emerald-300" data-testid={`document-mark-${d.id}`}>MARK {d.mydata.mark}</div>}
                  </td>
                  <td className="px-4 py-3"><TypeBadge type={d.type} /></td>
                  <td className="px-4 py-3 font-medium">{d.contact_name}</td>
                  <td className="px-4 py-3 hidden md:table-cell font-mono text-slate-600 dark:text-slate-300">{fmtDate(d.issue_date, lang)}</td>
                  <td className="px-4 py-3 hidden lg:table-cell font-mono text-slate-600 dark:text-slate-300">{fmtDate(d.due_date, lang)}</td>
                  <td className="px-4 py-3"><StatusBadge doc={d} /></td>
                  <td className="px-4 py-3 text-right font-mono font-medium">{fmtMoney(d.total)}</td>
                  <td className={`px-4 py-3 text-right font-mono hidden md:table-cell ${docBalance(d) > 0 && FINANCIAL_TYPES.includes(d.type) ? "text-amber-700 dark:text-amber-300" : "text-slate-400"}`} data-testid={`document-balance-${d.id}`}>
                    {FINANCIAL_TYPES.includes(d.type) ? fmtMoney(docBalance(d)) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Link to={`/documents/${d.id}`} data-testid={`document-view-${d.id}`} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500"><Eye size={15} /></Link>
                      <Link to={`/documents/${d.id}/edit`} data-testid={`document-edit-${d.id}`} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500"><Pencil size={15} /></Link>
                      <button onClick={() => setDeleting(d)} data-testid={`document-delete-${d.id}`} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-rose-100 dark:hover:bg-rose-950 text-rose-600"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {docs.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-500" data-testid="documents-empty">{t("no_results")}</td></tr>
              )}
            </tbody>
            {docs.length > 0 && (
              <tfoot>
                <tr className="bg-slate-50 dark:bg-slate-900/60 font-mono font-medium border-t-2 border-slate-200 dark:border-slate-700">
                  <td colSpan={6} className="px-4 py-3 font-body text-slate-500">{t("group_total")} · {docs.length}</td>
                  <td className="px-4 py-3 text-right" data-testid="documents-total-sum">{fmtMoney(totals.total)}</td>
                  <td className="px-4 py-3 text-right hidden md:table-cell text-amber-700 dark:text-amber-300" data-testid="documents-balance-sum">{fmtMoney(totals.balance)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} message={t("confirm_delete_doc")} onConfirm={remove} />
    </div>
  );
}
